import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Form, Textarea, useToast } from '@crisellevaldez/speculo-ui';
import { PauseCircle, AlertTriangle } from 'lucide-react';
import { pauseContact } from '../../../../api/contacts';

const REASON_MAX_LENGTH = 247;

interface PauseContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  contactId: string;
  organizationId: string;
  contactName: string;
  assistantName?: string;
  /**
   * Surface the CRM-sync-clobber heads-up. True when the contact lives in a
   * CRM allowed-stage that the inbound sync could move back to an active
   * state — flagged by the parent so this dialog stays presentation-only.
   */
  syncClobberWarning?: boolean;
  /** Fires after a successful pause with the raw reason the user entered
   *  (no `manual:` prefix). The parent can use this to optimistically
   *  update local state and trigger a contact refetch. */
  onPaused: (rawReason: string) => void;
}

export const PauseContactDialog: React.FC<PauseContactDialogProps> = ({
  isOpen,
  onClose,
  contactId,
  organizationId,
  contactName,
  assistantName = 'Remi',
  syncClobberWarning,
  onPaused,
}) => {
  const { showToast } = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const trimmed = reason.trim();
  const overLimit = trimmed.length > REASON_MAX_LENGTH;
  const canSubmit = trimmed.length > 0 && !overLimit && !isSubmitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await pauseContact(contactId, organizationId, trimmed);
      showToast({ message: `${assistantName} is now paused for ${contactName}.`, variant: 'success' });
      onPaused(trimmed);
      onClose();
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to pause contact. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <Modal.Header>
        <div className="flex items-center gap-3">
          <PauseCircle className="w-6 h-6 text-foreground/70" />
          <h3 className="text-lg font-bold text-foreground">Pause {assistantName}?</h3>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-3">
          <Typography variant="p" className="text-foreground/80 text-sm">
            {assistantName} will stop all outreach for{' '}
            <strong className="text-foreground">{contactName}</strong>. The contact's opt-in is not
            affected. You can resume {assistantName} for them at any time.
          </Typography>

          <div className="flex flex-col gap-1.5">
            <Form.Label htmlFor="pause-reason">Reason</Form.Label>
            <Textarea
              id="pause-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Lead requested no further contact"
              maxLength={REASON_MAX_LENGTH + 10}
              rows={3}
              autoFocus
              disabled={isSubmitting}
            />
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>Stored on the contact and shown on the status card.</span>
              <span
                className={
                  overLimit ? 'text-red-600 dark:text-red-300 font-semibold' : undefined
                }
              >
                {trimmed.length}/{REASON_MAX_LENGTH}
              </span>
            </div>
          </div>

          {syncClobberWarning && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300/60 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-700 dark:text-amber-300 flex-shrink-0" />
              <p className="text-[11.5px] leading-snug text-amber-800 dark:text-amber-200">
                This contact is in a CRM stage that re-activates outreach on sync. The pause may be
                cleared the next time the CRM syncs — consider removing the{' '}
                <code className="px-1 rounded bg-amber-100 dark:bg-amber-500/20">
                  speculo_opt_in
                </code>{' '}
                tag or moving them out of that stage to keep this pause durable.
              </p>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-600 dark:text-red-300 leading-snug">{error}</p>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="bg-red-700 hover:bg-red-600 text-white border-red-700"
            onClick={handleConfirm}
            disabled={!canSubmit}
            isLoading={isSubmitting}
          >
            Confirm pause
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
